// @ts-nocheck — W8.2: App.tsx migrado do legado class-based para funcional React 19
import React, { useEffect } from "react";
import { Navigate, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import * as containerAction from "./actions/Container";

// Finance pages — W12.1: migrados para hooks nativos, sem withRouterV6
import ImovelList from "./pages/Finance/Imovel/List";
import ImovelView from "./pages/Finance/Imovel/View";
import LoteView from "./pages/Finance/Lote/View";
import LogradouroList from "./pages/Finance/Logradouros/List";
import LogradouroView from "./pages/Finance/Logradouros/View";
import ArquivosDigitalizadosList from "./pages/Finance/ArquivosDigitalizados/List";
import ArquivosGeraisFinancasList from "./pages/Finance/ArquivosGeraisFinancas/ListView";
import VisualizadorGatRedeSim from "./pages/Finance/Imovel/VisualizadorGatRedeSim";

// Layout
import PublicLayout from "./layouts/PublicLayout";
import NavegadorShell from "./components/Navegador/NavegadorShell";
import LogradourosShell from "./components/Logradouros/LogradourosShell";

function AppBootstrapper() {
  const dispatch = useDispatch();
  const publicSettings = useSelector((state: any) => state.publicSettings.settings);
  const config = useSelector((state: any) => state.container.config);
  // Ref atualizada toda vez que publicSettings muda — usada pelo timer do mock
  // dev fallback que roda com closure [] e por isso não vê updates de selector.
  const publicSettingsLatestRef = React.useRef<any>(null);
  useEffect(() => { publicSettingsLatestRef.current = publicSettings; }, [publicSettings]);

  useEffect(() => {
    if (!publicSettings) {
      dispatch(containerAction.getPublicSettings(() => {
        // callback: if publicSettings loaded, getConfig
      }));

      // Dev fallback: if API not reachable after 4s, inject mock data.
      // Check via uma ref atualizada por effect separado — o snapshot via
      // closure ([] deps) sempre via null. Sem isso o mock SEMPRE dispara
      // mesmo com API local respondendo, sobrescrevendo dados reais
      // (notavelmente mapGoogleMapsKey vazio → tile.googleapis.com 400).
      if (import.meta.env.DEV) {
        const timer = setTimeout(() => {
          if (publicSettingsLatestRef.current) return; // settings real chegou
          dispatch({
            type: "public_settings",
            payload: {
              publicActive: true,
              navegadorGeofinanceiroActive: true,
              logradouroActive: true,
              arquivosDigitalizadosActive: true,
              arquivosGeraisActive: true,
              navegadorGeofinanceiroAbaName: "Navegador GeoFinanceiro",
              logradouroAbaName: "Logradouros",
              arquivosDigitalizadosAbaName: "Arquivos Digitalizados",
              arquivosGeraisAbaName: "Arquivos Gerais",
              opcaoExibicaoMenu: "[ABA]",
              mapaInterativoActive: false,
            },
          });
          dispatch({
            type: "current_config",
            payload: {
              systemName: "SmartGIS Público",
              systemFooterText: "Tecnomapas. Todos os direitos reservados.",
              clientName: "Município",
              styleCss: null,
              systemFinanceSyncLastDate: null,
            },
          });
          dispatch({
            type: "update_version",
            payload: "2.0.0-dev",
          });
        }, 4000);
        return () => clearTimeout(timer);
      }
    }
  }, []);

  useEffect(() => {
    if (publicSettings?.publicActive && !config) {
      dispatch(containerAction.getConfig());
      dispatch(containerAction.getVersion());
    }
  }, [publicSettings]);

  useEffect(() => {
    if (config) {
      document.title = `${config.systemName} - Público`;
    }
  }, [config]);

  if (!publicSettings) {
    return null;
  }

  if (!publicSettings.publicActive) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        O módulo público está desabilitado. Contate o administrador do sistema.
      </div>
    );
  }

  if (!config) {
    return null;
  }

  const {
    navegadorGeofinanceiroActive,
    logradouroActive,
    arquivosDigitalizadosActive,
    arquivosGeraisActive,
  } = publicSettings;

  return (
    <>
      <Routes>
        {/* Rota especial do VisualizadorGatRedeSim — sem layout padrão */}
        <Route path="/unidade/visualizar/:inscricao" element={<VisualizadorGatRedeSim />} />

        {/* Rotas principais dentro do PublicLayout */}
        <Route element={<PublicLayout />}>
          {/* Navegador Geofinanceiro — SmartMap persistente via NavegadorShell.
              SmartMap monta UMA vez; List/View/LoteView entram via <Outlet/> na
              sidebar lateral. Mapa preserva zoom/pan/basemap entre navegações. */}
          {navegadorGeofinanceiroActive && (
            <>
              <Route element={<NavegadorShell baseRoute="/" />}>
                <Route path="/" element={<ImovelList />} />
              </Route>
              <Route element={<NavegadorShell baseRoute="/navegador-geofinanceiro" />}>
                <Route path="/navegador-geofinanceiro" element={<ImovelList />} />
                <Route path="/navegador-geofinanceiro/imovel/:id" element={<ImovelView />} />
                <Route path="/navegador-geofinanceiro/lote/:id" element={<LoteView />} />
              </Route>
            </>
          )}

          {/* Logradouros — mesmo padrão shell-first */}
          {logradouroActive && (
            <Route element={<LogradourosShell baseRoute="/logradouro" />}>
              <Route path="/logradouro" element={<LogradouroList />} />
              <Route path="/logradouro/visualizar/:id" element={<LogradouroView />} />
            </Route>
          )}

          {/* Arquivos Digitalizados */}
          {arquivosDigitalizadosActive && (
            <Route path="/arquivos-digitalizados" element={<ArquivosDigitalizadosList />} />
          )}

          {/* Arquivos Gerais Finanças */}
          {arquivosGeraisActive && (
            <Route path="/arquivos-gerais-financas" element={<ArquivosGeraisFinancasList />} />
          )}

          {/* Fallback — vai para raiz */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>

      {config.styleCss && (
        <style type="text/css">{config.styleCss}</style>
      )}
    </>
  );
}

export default function App() {
  return <AppBootstrapper />;
}
